/**
 * Real, linkable routes (`createBrowserRouter`).
 *
 * The guard is the interesting part. It reads the session through
 * `session.useSession()` — `useSyncExternalStore` underneath — so **clearing
 * the session actually re-renders**. A guard that reads `localStorage` during
 * render subscribes to nothing: the app would go on rendering while every
 * request 401s (SSO_INTEGRATION §3.4, the second of the three mistakes).
 *
 * **What is routed is what has a service route** (plan §7). Review came back
 * in W4, behind `can_update` on `document`; Tags in W5, behind `can_update` on
 * `tag` (FR-14 — managing the list is the administrator's); Settings in W6,
 * behind `can_view` on `settings` — **since W28 (FR-65, D85) a hub and a page
 * per area under it**, `/settings/<area>`. **A share link's page,
 * `/s/:token`, sits outside the guard** (W6, FR-29): its reader may have no
 * FiveOX account, and the page asks for a sign-in only when the link does. The
 * prototype's tasks, calendar, automations and messaging are never ProDoc's
 * (ADR-0011) and were deleted in W3 (T3.1). **Engine W15** adds an
 * extraction's own address under its document, behind `can_view` on
 * `extraction`, and the extraction queue, behind `can_update` on it.
 * **W22 (FR-59, D57)**: upload is a popup over the current screen, so
 * `/upload` is no longer a screen — the address opens Documents with the popup
 * open (`?upload=1`, read once and dropped), and no link or bookmark breaks.
 */

import { SsoRedirect } from '@fiveox/shell'
import { useEffect, type ReactNode } from 'react'
import { Navigate, Outlet, createBrowserRouter } from 'react-router'

import { App } from './App'
import { useOrg } from './components/org-context'
import { DOCUMENT, EXTRACTION, SETTINGS, TAG, WORKSPACE } from './lib/access'
import { RETURN_KEY, ssoConfig } from './lib/sso-config'
import { session } from './lib/session'
import { armSessionEnd } from './lib/session-end'
import { Callback } from './routes/Callback'
import { TagsPage } from './routes/admin/tags'
import { WorkspacesPage } from './routes/admin/workspaces'
import { DocumentDetailPage } from './routes/document-detail'
import { DocumentsPage } from './routes/documents'
import { ExtractionPage } from './routes/extraction'
import { ExtractionQueuePage } from './routes/extractions'
import { HomePage } from './routes/home'
import { ReviewPage } from './routes/review'
import { ErasureSettings } from './routes/settings/erasure'
import { SettingsRoute } from './routes/settings/host'
import { SettingsHub } from './routes/settings/hub'
import { ParsingSettings } from './routes/settings/parsing'
import { RetentionSettings } from './routes/settings/retention'
import { ReviewSettings } from './routes/settings/review'
import { SharingSettings } from './routes/settings/sharing'
import { TypesSettings } from './routes/settings/types'
import { UsageSettings } from './routes/settings/usage'
import { SharedLinkPage } from './routes/shared-link'

function RequireSession({ children }: { children: ReactNode }) {
  const current = session.useSession()

  // ADR-0007: the watchdog, the scoped sign-out listener and the org clear are
  // armed **once**, from the component that already owns the *no session → SSO*
  // decision and is mounted exactly once. The effect's cleanup is the disarm.
  useEffect(() => armSessionEnd(), [])

  // No session — hand the browser to accounts. **This app has no login screen**
  // (ADR-0048); `SsoRedirect` stashes the deep link and starts the PKCE flow.
  if (!current) {
    return <SsoRedirect config={ssoConfig()} returnKey={RETURN_KEY} />
  }
  return <>{children}</>
}

function Shell() {
  return (
    <RequireSession>
      <App>
        <Outlet />
      </App>
    </RequireSession>
  )
}

/** A route that exists only for some readers. Withheld, it **redirects**
 *  rather than rendering empty — a bookmark must land somewhere true. */
function RequireVerb({ resource, verb, children }: { resource: string; verb: 'can_view' | 'can_create' | 'can_update' | 'can_delete'; children: ReactNode }) {
  const { can } = useOrg()
  return can(resource, verb) ? <>{children}</> : <Navigate to="/" replace />
}

export const router = createBrowserRouter([
  // `/callback` sits **outside** the guard: it is the route that creates the
  // session, so guarding it would redirect the sign-in away from its own
  // completion.
  { path: '/callback', element: <Callback /> },
  // `/s/<token>` too: what a share link opens, for a reader with or without an
  // account (FR-29). The token never leaves this address — the page sends it
  // as a header (plan §5.3).
  { path: '/s/:token', element: <SharedLinkPage /> },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'documents', element: <DocumentsPage /> },
      { path: 'documents/:id', element: <DocumentDetailPage /> },
      // Engine W15: one extraction — read by every role that may (`extraction:can_view`, the
      // service refuses the rest), corrected and decided by `extraction:can_update`.
      {
        path: 'documents/:id/extractions/:eid',
        element: (
          <RequireVerb resource={EXTRACTION} verb="can_view">
            <ExtractionPage />
          </RequireVerb>
        ),
      },
      {
        // Engine W15 (FR-E20): what waits for a person — the reviewer's and the admin's.
        path: 'extractions',
        element: (
          <RequireVerb resource={EXTRACTION} verb="can_update">
            <ExtractionQueuePage />
          </RequireVerb>
        ),
      },
      // W22 (FR-59): the popup, over Documents — an old link or bookmark still lands on the upload.
      { path: 'upload', element: <Navigate to="/documents?upload=1" replace /> },
      {
        // The open document rides in `?doc=`; deciding is `can_update` on
        // `document` (plan §5.2), so a reader without it is sent home.
        path: 'review',
        element: (
          <RequireVerb resource={DOCUMENT} verb="can_update">
            <ReviewPage />
          </RequireVerb>
        ),
      },
      {
        // Settings (W6, FR-36): read by every role, changed by `settings:can_update`. W28 (FR-65,
        // D85): the hub, then a page per area, each given the host once by `SettingsRoute`. Usage and
        // erasure say on their own page when they are not this person's; an unknown area lands on the hub.
        path: 'settings',
        element: (
          <RequireVerb resource={SETTINGS} verb="can_view">
            <SettingsRoute />
          </RequireVerb>
        ),
        children: [
          { index: true, element: <SettingsHub /> },
          { path: 'types', element: <TypesSettings /> },
          { path: 'review', element: <ReviewSettings /> },
          { path: 'retention', element: <RetentionSettings /> },
          { path: 'sharing', element: <SharingSettings /> },
          { path: 'parsing', element: <ParsingSettings /> },
          { path: 'usage', element: <UsageSettings /> },
          { path: 'erasure', element: <ErasureSettings /> },
          { path: '*', element: <Navigate to="/settings" replace /> },
        ],
      },
      {
        path: 'admin',
        children: [
          { index: true, element: <Navigate to="/admin/workspaces" replace /> },
          {
            path: 'workspaces',
            element: (
              <RequireVerb resource={WORKSPACE} verb="can_create">
                <WorkspacesPage />
              </RequireVerb>
            ),
          },
          {
            path: 'tags',
            element: (
              <RequireVerb resource={TAG} verb="can_update">
                <TagsPage />
              </RequireVerb>
            ),
          },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
